import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";

import { Store } from "@ngrx/store";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { filter, first, map, take } from "rxjs/operators";
import {
  getProfile,
  selectUsername,
  selectFailedInvoice,
  getAccountInfo,
  selectSoftCancel,
  selectAccountAndMembershipStatus,
  selectAccountInfo,
  selectMarketingContents,
  getMarketingContent,
  selectB2bBenefit,
  getB2bBenefit,
  selectStayAndSaveSubscriptionDiscount,
} from "src/app/core/ngrx/myqq";
import {
  AccountSetupSteps,
  UpdateFlockPresaleVehicleSteps,
  selectShowFlockPresaleModal,
  setAccountSetupStep,
  setUIPromoCode,
  setUpdateFlockPresaleVehicleStep,
} from "src/app/core/ngrx/ui";
import { OfflineAuthService } from "src/app/core/services/offline-auth.service";
import { WINDOW } from "src/app/core/services/window.service";
import { scaleInOut } from "src/app/shared/utilities/animations";
import { environment } from "src/environments/environment";
import {
  combineS,
  filterSuccessMapToRightValue,
  withDatumEither,
} from "src/lib/datum-either";
import { AccountSetupModalComponent } from "src/app/shared/components/account-setup-modal/account-setup-modal.component";
import { UpdateFlockPresaleVehiclePage } from "./update-flock-presale-vehicle/update-flock-presale-vehicle.page";
import { MarketingContent } from "src/app/core/services/myqq";
import { UnleashService } from "src/app/shared/services/unleash.service";
import { Meta, Title } from "@angular/platform-browser";

@Component({
  selector: "myqq-home",
  templateUrl: "./home.page.html",
  styleUrls: ["./home.page.scss"],
  animations: [scaleInOut("rollDownUp", 200)],
})
export class HomePage implements OnDestroy, OnInit {
  readonly name$ = this.store$
    .select(selectUsername)
    .pipe(filterSuccessMapToRightValue);
  readonly failedInvoice$ = this.store$
    .select(selectFailedInvoice)
    .pipe(filterSuccessMapToRightValue, take(1));
  readonly softCancel$ = this.store$
    .select(selectSoftCancel)
    .pipe(filterSuccessMapToRightValue, take(1));

  readonly accountAndMembershipStatus$ = this.store$
    .select(selectAccountAndMembershipStatus)
    .pipe(filterSuccessMapToRightValue);

  readonly stayAndSaveSubscriptionDiscount$ = this.store$
    .select(selectStayAndSaveSubscriptionDiscount)
    .pipe(filterSuccessMapToRightValue);
  readonly nextStep$ = this.accountAndMembershipStatus$.pipe(
    take(1),
    map((value) => {
      if (!value.hasAccount) {
        this.handleNextStep("/account-setup");
        return {
          title: "Purchase or Locate Membership",
          route: "/account-setup",
        };
      } else if (value.hasMembership) {
        return {
          title: "View Your Membership",
          route: "/membership",
        };
      } else {
        return {
          title: "Purchase Membership",
          route: "/purchase",
        };
      }
    })
  );

  readonly marketingContents$ = this.store$
    .select(selectMarketingContents)
    .pipe(filterSuccessMapToRightValue);

  timeoutDuration = 500;
  marketingContents = [];
  authenticated: BehaviorSubject<boolean>;
  subs: Subscription[] = [];
  cdnHost = environment.cdnHost;
  enableB2bFlag$: Observable<boolean> = this.unleashService.enableB2b$;

  constructor(
    private store$: Store<any>,
    readonly router: Router,
    private offlineAuth: OfflineAuthService,
    private unleashService: UnleashService,
    @Inject(WINDOW) private window: Window,
    readonly dialog: MatDialog,
    private titleService: Title,
    private metaService: Meta
  ) {
    this.authenticated = this.offlineAuth.allowAccess$;
  }

  readonly b2bBenefit$ = this.store$
    .select(selectB2bBenefit)
    .pipe(filterSuccessMapToRightValue);

  ngOnInit() {
    this.titleService.setTitle("Home | myQQ");
    this.metaService.updateTag({
      name: "description",
      content:
        "Quick Quack, an automatic car wash with Unlimited Memberships and Free Vacuums in California, Texas, Arizona, Colorado, and Utah. Don't Drive Dirty!",
    });
    this.subs.push(
      this.authenticated
        .pipe(
          filter((auth) => auth),
          first()
        )
        .subscribe(() => {
          this.handleUpdateVehiclePrompt();
          this.handleGetData();
        })
    );

    // go back to landing page if unuath
    if (!this.authenticated.value) {
      this.router.navigateByUrl("");
    }

    this.subs.push(
      this.enableB2bFlag$
        .pipe(
          filter((val) => val),
          first()
        )
        .subscribe(() => {
          if (this.authenticated.value) {
            this.store$.dispatch(getB2bBenefit.pending(null));
          }
        })
    );
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  handleGetData() {
    this.store$.dispatch(getProfile.pending(null));
    this.store$.dispatch(getAccountInfo.pending(null));
    this.store$.dispatch(getMarketingContent.pending(null));
  }

  async handleNextStep(route: string) {
    if (route == "/account-setup") {
      const steps = AccountSetupSteps;
      const accountSetupModal = await this.dialog.open(
        AccountSetupModalComponent
      );
      this.subs.push(
        await accountSetupModal.afterClosed().subscribe((result) => {
          if (!!result) {
            this.store$.dispatch(
              setAccountSetupStep(
                result?.member ? steps.last4lookup : steps.enter_account_details
              )
            );
            this.router.navigateByUrl(route);
          }
        })
      );
    } else {
      this.router.navigateByUrl(route);
    }
  }

  handleFailedPayment(route: any[]) {
    this.router.navigate(route);
  }

  emailSupport() {
    this.window.open(
      `mailto:${environment.supportEmail}?subject="Reactivate Quick Quack Unlimited Membership`,
      "_blank"
    );
  }

  handleWhyQQ() {
    this.router.navigateByUrl("/info");
  }

  handleClickMarketingCard(marketingCard: MarketingContent) {
    if (!!marketingCard.promoCode) {
      this.store$.dispatch(setUIPromoCode(marketingCard.promoCode));
    }
    if (!!marketingCard.link) {
      this.subs.push(
        this.accountAndMembershipStatus$
          .pipe(first())
          .subscribe((status) =>
            status.hasAccount
              ? this.router.navigateByUrl(marketingCard.link)
              : this.handleNextStep("/account-setup")
          )
      );
    }
  }

  handleRelink({ hasAccount, hasMembership }) {
    if (!hasMembership && hasAccount) {
      this.router.navigateByUrl("/relink");
    } else if (!hasMembership && !hasAccount) {
      const steps = AccountSetupSteps;
      this.store$.dispatch(setAccountSetupStep(steps.last4lookup));
      this.router.navigateByUrl("/account-setup");
    }
  }

  handleUpdateVehiclePrompt() {
    this.subs.push(
      combineS({
        showUpdateFlockPresaleModal: this.store$
          .select(selectShowFlockPresaleModal)
          .pipe(withDatumEither()),
        accountInfo: this.store$.select(selectAccountInfo),
      })
        .pipe(filterSuccessMapToRightValue, first())
        .subscribe(({ showUpdateFlockPresaleModal, accountInfo }) => {
          if (
            showUpdateFlockPresaleModal &&
            !!accountInfo?.account?.vehicles.find(
              (vehicle) => vehicle.isTemporaryIdentifier
            )
          ) {
            this.store$.dispatch(
              setUpdateFlockPresaleVehicleStep(
                UpdateFlockPresaleVehicleSteps.alert
              )
            );
            this.dialog.open(UpdateFlockPresaleVehiclePage);
          }
        })
    );
  }
}
